import { RefObject, useEffect, useRef } from 'react';

/**
 * Keeps a scrollable message list pinned to the bottom without stealing the
 * user's scroll position.
 *
 * Two things it deliberately does *not* do:
 *
 * - It never scrolls just because a fetch settled. Polled message lists flip a
 *   loading flag on every refresh; scrolling on that alone yanked the viewport
 *   to the bottom every few seconds even when the poll returned nothing new.
 * - It never scrolls when the user has scrolled up to read history. New
 *   messages only pull the viewport down if it was already sitting at (or
 *   within a few dozen pixels of) the bottom.
 *
 * `resetKey` identifies the conversation being shown — changing it forces a
 * scroll to the bottom regardless of where the previous conversation was
 * parked.
 */

/** How far off the bottom still counts as "the user is following along". */
const BOTTOM_THRESHOLD_PX = 64;

export function useStickToBottom(
  viewportRef: RefObject<HTMLDivElement | null>,
  itemCount: number,
  resetKey?: string
): void {
  const isAtBottomRef = useRef(true);
  const lastCountRef = useRef(0);
  const lastResetKeyRef = useRef(resetKey);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) {
      return;
    }
    const onScroll = () => {
      isAtBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD_PX;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
    };
  }, [viewportRef]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) {
      return;
    }

    const conversationChanged = resetKey !== lastResetKeyRef.current;
    const grew = itemCount > lastCountRef.current;
    lastResetKeyRef.current = resetKey;
    lastCountRef.current = itemCount;

    if (conversationChanged) {
      isAtBottomRef.current = true;
    }
    if (!itemCount) {
      return;
    }
    // A poll that returned the same messages, or a user reading back through
    // history — leave the viewport where it is.
    if (!conversationChanged && (!grew || !isAtBottomRef.current)) {
      return;
    }

    // Wait one frame so layout (drawer transitions, bubble wrapping) has
    // settled before measuring scrollHeight; otherwise the scroll lands
    // halfway up an unfinished list.
    const raf = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
      isAtBottomRef.current = true;
    });
    return () => cancelAnimationFrame(raf);
  }, [viewportRef, itemCount, resetKey]);
}
